import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import useDataSourceManager from "hooks/useDataSourceManager";
import { styled, useTheme } from "@mui/material/styles";
import { Box, CircularProgress, Typography } from "@mui/material";
import { SolidButton } from "components/buttons/SolidRectangleButton";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import GlobalAlert from "components/Notification";

import CollapseCard from "components/Collapse";
import ModalContainer from "components/ModalContainer";
import { getAppApiURL } from "utils/serverUrl";

const versionURL = `${getAppApiURL().replace(/\/$/, "")}/unauth/proxyConfig`;

const TitleText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.customFontSize[2],
  color: theme.palette.primary.black,
  fontWeight: theme.typography.customFontWeight[1],
}));

const DescText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.customFontSize[2],
  color: theme.palette.primary.black,
  marginTop: "8px",
}));

const LinkText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.customFontSize[2],
  color: theme.palette.primary.black,
  textDecoration: "underline",
  cursor: "pointer",
  width: "fit-content",
}));

// 比較版本高低
const compareVersion = (v1 = "", v2 = "") => {
  const arr1 = String(v1)
    .split(".")
    .map((item) => Number(item) || 0);
  const arr2 = String(v2)
    .split(".")
    .map((item) => Number(item) || 0);
  const maxLength = Math.max(arr1.length, arr2.length);

  for (let i = 0; i < maxLength; i += 1) {
    const num1 = arr1[i] || 0;
    const num2 = arr2[i] || 0;

    if (num1 > num2) return 1;
    if (num1 < num2) return -1;
  }

  return 0;
};

const SoftwareUpdatePanel = ({ loading, infoData }) => {
  const theme = useTheme();
  const { dataMap, getData, sendRequest } = useDataSourceManager();

  const [versionData, setVersionData] = useState(null);
  const [versionLoading, setVersionLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openReleaseNoteModal, setOpenReleaseNoteModal] = useState(false);
  const [updateExecuteCommandUuid] = useState(uuidv4());
  const [isUpdating, setIsUpdating] = useState(false);

  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: "",
    message: "",
  });

  const latestVersion = versionData?.version || "";
  const currentVersion = infoData?.firmwareVersion || "";
  const hasNewVersion = compareVersion(latestVersion, currentVersion) === 1;

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        setVersionLoading(true);
        const res = await fetch(versionURL);
        const data = await res.json();
        setVersionData(data);
      } catch (err) {
        console.error("version fetch failed:", err);
      } finally {
        setVersionLoading(false);
      }
    };

    fetchVersion();
  }, []);

  const handleUpdateConfirm = (commandName) => {
    const breadcrumbs = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}",
    );
    const hasManageOrg = breadcrumbs.realm?.hasManageOrg === true;
    const realmId = breadcrumbs.realm?.id ?? null;
    const orgsId = breadcrumbs.orgs?.id ?? null;
    const siteId = breadcrumbs.site?.id ?? null;

    let entId = null;
    let orgId = null;

    if (hasManageOrg) {
      entId = realmId;
      if (orgsId) orgId = orgsId;
    } else {
      orgId = realmId;
    }

    const resourcePath =
      hasManageOrg && orgId
        ? "/ents/:entId/orgs/:orgId/sites/:siteId/devices/:deviceId/executeCommand.update"
        : "/orgs/:orgId/devices/:deviceId/executeCommand.update";

    const restfulParams =
      hasManageOrg && orgId
        ? { entId, orgId, siteId, deviceId: infoData.id }
        : { orgId, siteId, deviceId: infoData.id };

    const requestParams = {
      data: {
        command: commandName,
        params: {},
      },
    };

    sendRequest(
      resourcePath,
      updateExecuteCommandUuid,
      restfulParams,
      requestParams,
    );

    setOpenModal(false);
  };

  useEffect(() => {
    const executeCommandResponse = getData(updateExecuteCommandUuid);
    if (!executeCommandResponse) return;

    console.log(executeCommandResponse);

    if (executeCommandResponse.code) {
      setAlertInfo({
        open: true,
        severity: "error",
        message: executeCommandResponse.message,
      });
      return;
    }

    setIsUpdating(true);
  }, [dataMap, getData, updateExecuteCommandUuid]);

  return (
    <>
      <CollapseCard title="Software Update" minWidth="550px">
        {loading || versionLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "120px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ margin: "15px" }}>
            {isUpdating ? (
              <>
                <TitleText>Updating to version: {latestVersion}</TitleText>

                <DescText>
                  This proxy is currently updating and will be ready in 10 mins
                </DescText>

                <LinkText
                  component="a"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpenReleaseNoteModal(true);
                  }}
                >
                  Release note
                </LinkText>
              </>
            ) : !hasNewVersion ? (
              <DescText>This Proxy is currently at latest version</DescText>
            ) : (
              <>
                <TitleText>New version: {latestVersion}</TitleText>

                <DescText>
                  You can now update the Unity Proxy to latest version.
                </DescText>

                <LinkText
                  component="a"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpenReleaseNoteModal(true);
                  }}
                >
                  Release note
                </LinkText>

                <Box sx={{ marginTop: "12px" }}>
                  <SolidButton
                    width="110px"
                    height="32px"
                    onClick={() => setOpenModal(true)}
                  >
                    Update now
                  </SolidButton>
                </Box>
              </>
            )}
          </Box>
        )}
      </CollapseCard>

      <ModalContainer display={openModal} modalWidth="500px">
        <Box sx={{ textAlign: "center" }}>
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontWeight: theme.typography.customFontWeight[3],
              fontSize: theme.typography.customFontSize[5],
            }}
          >
            Update {infoData.deviceName}
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginTop: "20px",
            }}
          >
            <Typography
              sx={{
                color: theme.palette.primary.black,
                fontSize: theme.typography.customFontSize[3],
                fontFamily: theme.typography.fontFamily,
                fontWeight: theme.typography.customFontWeight[0],
                whiteSpace: "pre-wrap",
              }}
            >
              You will now update proxy {infoData.deviceName}. Devices connected
              to this proxy may be temporarily offline for about 10 minutes.
            </Typography>
          </Box>

          <Box
            sx={{
              paddingTop: "20px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <OutlineRectangleButton
              width="90px"
              height="32px"
              sx={{ marginRight: "20px" }}
              onClick={() => setOpenModal(false)}
            >
              Cancel
            </OutlineRectangleButton>

            <SolidButton
              width="90px"
              height="32px"
              onClick={() => handleUpdateConfirm("fwUpdate")}
            >
              Update
            </SolidButton>
          </Box>
        </Box>
      </ModalContainer>

      <ModalContainer display={openReleaseNoteModal} modalWidth="600px">
        <Box>
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontWeight: theme.typography.customFontWeight[3],
              fontSize: theme.typography.customFontSize[5],
              textAlign: "center",
            }}
          >
            Release note
          </Typography>

          <Box
            sx={{
              marginTop: "20px",
              maxHeight: "400px",
              overflowY: "auto",
            }}
            dangerouslySetInnerHTML={{
              __html: versionData?.notes || "",
            }}
          />

          <Box
            sx={{
              paddingTop: "20px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <SolidButton
              width="90px"
              height="32px"
              onClick={() => setOpenReleaseNoteModal(false)}
            >
              Close
            </SolidButton>
          </Box>
        </Box>
      </ModalContainer>

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default SoftwareUpdatePanel;
